// src/utils/techDailyPay.js
//
// TECH DAILY PAY: the one place that says what a tech's day of work costs.
// Every accounting page that needs labor numbers reads them through here, so
// the Tech Daily Pay page, Job Estimate Spreadsheet, Expenses, Cash Plan,
// Profit & Profit Sharing, Accounting Exports and the Financials dashboard all
// agree.
//
// PAY RATE (from the Staff page, users/{uid})
//   salary          Work Hourly Rate (the existing field is an HOURLY rate)
//   yearlySalary    fallback: yearly / 2080
//   hoursPerDay     paid hours in a normal day (default 8)
//   dailyPayRate    optional flat day rate; when set it wins over hourly x hours
//
// ONE DAY = dailyLabor/{YYYY-MM-DD}
//   date            "YYYY-MM-DD"
//   attendance      { [userId]: true|false }       worked that day?
//   hours           { [userId]: number }           hours (from Clock In/Out)
//   payOverrides    { [userId]: number }           manual day pay
//   assignments     { [userId]: string[] }         jobIds worked that day
//   workers         [{ userId, name, worked, hours, dailyPay, jobIds }]
//   jobCosts        { [jobId]: number }            each worker's pay split
//                                                  evenly over their jobs
//   unassignedCost  number                         pay with no job picked
//   totalCost       number                         sum of worked dailyPay
//   updatedAt
//
// Older days (saved before this file existed) only have attendance /
// payOverrides / assignments / totalCost. They are still read correctly:
// pay is recomputed from the Staff rate when `workers` is missing.

import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";

export const DAILY_LABOR = "dailyLabor";
export const DEFAULT_HOURS_PER_DAY = 8;
export const UNASSIGNED_JOB = "__unassigned";

export const numberValue = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export const roundMoney = (value) => Math.round(numberValue(value) * 100) / 100;

export const staffName = (user = {}) =>
  `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
  user.name ||
  user.fullName ||
  user.email ||
  "";

// The Staff page's "salary" field is an HOURLY rate. Never treat it as a
// yearly salary or as a day rate.
export const getHourlyPayRate = (user = {}) => {
  const hourly = numberValue(user.salary);
  if (hourly > 0) return hourly;
  const other = numberValue(user.hourlyRate || user.payRate || user.hourly || user.rate);
  if (other > 0) return other;
  const yearly = numberValue(user.yearlySalary || user.annualSalary);
  if (yearly > 0) return yearly / 2080;
  return 0;
};

export const getHoursPerDay = (user = {}) =>
  numberValue(user.hoursPerDay) || DEFAULT_HOURS_PER_DAY;

// What a normal full day costs for this person.
export const getDefaultDailyPay = (user = {}) => {
  const flat = numberValue(user.dailyPayRate);
  if (flat > 0) return roundMoney(flat);
  return roundMoney(getHourlyPayRate(user) * getHoursPerDay(user));
};

// Pay for a given number of worked hours (used when hours come from the clock).
export const getPayForHours = (user = {}, hours) => {
  const flat = numberValue(user.dailyPayRate);
  if (flat > 0) return roundMoney(flat);
  return roundMoney(getHourlyPayRate(user) * numberValue(hours));
};

export const isSubContractor = (user = {}) =>
  String(user.workerType || "").toLowerCase().replace(/\s+/g, "").includes("subcontractor");

export const isDeactivated = (user = {}) => {
  const status = String(user.status || "").toLowerCase();
  return user.isActive === false || ["deactivated", "inactive", "terminated"].includes(status);
};

// ---------------------------------------------------------------------------
// Reading a saved day
// ---------------------------------------------------------------------------

// Normalized worker rows for one dailyLabor doc. `usersById` is only needed
// for older docs that don't have a `workers` array yet.
export const dayWorkers = (dayDoc = {}, usersById = {}) => {
  if (Array.isArray(dayDoc.workers) && dayDoc.workers.length) {
    return dayDoc.workers.map((w) => ({
      userId: w.userId || "",
      name: w.name || staffName(usersById[w.userId] || {}),
      worked: w.worked !== false,
      hours: numberValue(w.hours),
      dailyPay: roundMoney(w.dailyPay),
      jobIds: Array.isArray(w.jobIds) ? w.jobIds.filter(Boolean) : [],
    }));
  }

  const attendance = dayDoc.attendance || {};
  const overrides = dayDoc.payOverrides || {};
  const hours = dayDoc.hours || {};
  const assignments = dayDoc.assignments || {};

  return Object.keys(attendance)
    .filter((userId) => attendance[userId])
    .map((userId) => {
      const user = usersById[userId] || {};
      const override = overrides[userId];
      const hasOverride = override !== undefined && override !== null && override !== "";
      const workedHours = numberValue(hours[userId]);
      const dailyPay = hasOverride
        ? roundMoney(override)
        : workedHours > 0
        ? getPayForHours(user, workedHours)
        : getDefaultDailyPay(user);
      return {
        userId,
        name: staffName(user),
        worked: true,
        hours: workedHours || getHoursPerDay(user),
        dailyPay,
        jobIds: Array.isArray(assignments[userId]) ? assignments[userId].filter(Boolean) : [],
      };
    });
};

// { [jobId]: amount } plus UNASSIGNED_JOB for pay with no job picked.
export const dayJobCosts = (dayDoc = {}, usersById = {}) => {
  const out = {};
  dayWorkers(dayDoc, usersById)
    .filter((w) => w.worked && w.dailyPay > 0)
    .forEach((w) => {
      const jobs = w.jobIds.length ? w.jobIds : [UNASSIGNED_JOB];
      const share = w.dailyPay / jobs.length;
      jobs.forEach((jobId) => {
        out[jobId] = roundMoney((out[jobId] || 0) + share);
      });
    });
  return out;
};

export const dayTotal = (dayDoc = {}, usersById = {}) => {
  const workers = dayWorkers(dayDoc, usersById);
  if (workers.length) {
    return roundMoney(
      workers.filter((w) => w.worked).reduce((sum, w) => sum + w.dailyPay, 0)
    );
  }
  return roundMoney(dayDoc.totalCost);
};

const inRange = (dateKey, startKey, endKey) =>
  Boolean(dateKey) && (!startKey || dateKey >= startKey) && (!endKey || dateKey <= endKey);

// Load every saved day (optionally only a date range).
export const fetchDailyLaborDays = async (startKey = "", endKey = "") => {
  const snap = await getDocs(collection(db, DAILY_LABOR));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data(), date: d.data().date || d.id }))
    .filter((d) => inRange(String(d.date).slice(0, 10), startKey, endKey))
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
};

export const usersToMap = (users = []) =>
  (Array.isArray(users) ? users : []).reduce((acc, u) => {
    if (u?.id) acc[u.id] = u;
    return acc;
  }, {});

// Everything the accounting pages need for a date range, in one pass.
//   total         $ of tech daily pay in the range
//   byJob         { jobId: $ }   (UNASSIGNED_JOB = not tied to a job)
//   byUser        { userId: { name, days, hours, amount } }
//   byDate        { "YYYY-MM-DD": $ }
//   byMonth       { "YYYY-MM": $ }
//   rows          one row per person per day (for exports)
export const summarizeDailyLabor = (days = [], usersById = {}, startKey = "", endKey = "") => {
  const result = {
    total: 0,
    byJob: {},
    byUser: {},
    byDate: {},
    byMonth: {},
    unassigned: 0,
    rows: [],
  };

  days.forEach((day) => {
    const date = String(day.date || day.id || "").slice(0, 10);
    if (!inRange(date, startKey, endKey)) return;

    const workers = dayWorkers(day, usersById).filter((w) => w.worked);
    const dayAmount = workers.length
      ? workers.reduce((sum, w) => sum + w.dailyPay, 0)
      : numberValue(day.totalCost);

    result.total += dayAmount;
    result.byDate[date] = roundMoney((result.byDate[date] || 0) + dayAmount);
    const month = date.slice(0, 7);
    result.byMonth[month] = roundMoney((result.byMonth[month] || 0) + dayAmount);

    workers.forEach((w) => {
      const entry = result.byUser[w.userId] || { name: w.name, days: 0, hours: 0, amount: 0 };
      entry.days += 1;
      entry.hours += w.hours;
      entry.amount = roundMoney(entry.amount + w.dailyPay);
      result.byUser[w.userId] = entry;
      result.rows.push({ date, ...w });
    });

    const jobCosts = dayJobCosts(day, usersById);
    Object.entries(jobCosts).forEach(([jobId, amount]) => {
      result.byJob[jobId] = roundMoney((result.byJob[jobId] || 0) + amount);
    });
  });

  result.total = roundMoney(result.total);
  result.unassigned = roundMoney(result.byJob[UNASSIGNED_JOB] || 0);
  return result;
};
